import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Action as WhatToDoAction,
  Feature as WhatToDoFeature,
  type ResultOption,
  postWhatToDo,
} from '@/apis/core/search/whatToDo/post/postWhatToDo';
import { createAskSession } from '@/apis/ask/askHistory/post/createAskSession';
import { invokeAsk } from '@/apis/ask/ask/post/invokeAsk';
import { AskType } from '@/apis/ask/ask/types';
import { formatDateTime } from '@/components/pages/ask/AskSession.utils';
import { useWorkspaceErrorHandler } from '@/hooks/useWorkspaceErrorHandler';
import {
  mapResultOptionToSuggestedAction,
  resolveResultOptionFromSuggestedSelection,
  type SuggestedActionPayload,
} from '@/utils/landingPage/whatToDoSuggestedActions';
import {
  ASK_OPEN_PROGRESS_ASKING,
  ASK_OPEN_PROGRESS_CREATING,
  ASK_OPEN_PROGRESS_GO_WORKSPACE,
  ASK_OPEN_PROGRESS_OPEN_ASK,
  ASK_OPEN_PROGRESS_PERSONA,
  DEFAULT_CHAT_PERSONA_ID,
} from './Landingpage.constants';
import type { WorkspaceDisplayInfo } from './Landingpage.types';
import {
  getResultOptionsFromResponse,
  normalizeError,
} from './Landingpage.utils';

type WorkspaceSummary = { id: string; workspaceType?: string };

export const useLandingPageWhatToDo = ({
  getAccessToken,
  setCurrentWorkspaceId,
  workspaces,
  getWorkspaceDisplayInfo,
  captureResultOptionsForHistory,
  clearCapturedResultOptions,
}: {
  getAccessToken: () => Promise<string>;
  setCurrentWorkspaceId: (id: string | null) => void;
  workspaces: WorkspaceSummary[];
  getWorkspaceDisplayInfo: (workspaceId: string | undefined | null) => WorkspaceDisplayInfo;
  captureResultOptionsForHistory: (options: ResultOption[]) => void;
  clearCapturedResultOptions: () => void;
}) => {
  const navigate = useNavigate();
  const { handleError, handleWarning } = useWorkspaceErrorHandler();

  const [lastResultOptions, setLastResultOptions] = useState<ResultOption[]>(
    [],
  );
  const [isOpeningInChat, setIsOpeningInChat] = useState(false);
  const [suggestedActionProgress, setSuggestedActionProgress] = useState<
    string | null
  >(null);

  const openNewAskSessionWithInput = useCallback(
    async ({
      workspaceId,
      inputText,
      defaultAskPromptId,
      onProgress,
    }: {
      workspaceId: string;
      inputText: string;
      defaultAskPromptId?: string;
      onProgress?: (message: string) => void;
    }) => {
      const title = `${formatDateTime(new Date())} LitiumTW Chat`;
      onProgress?.(ASK_OPEN_PROGRESS_CREATING);
      const response = await createAskSession({
        getAccessToken,
        data: {
          workspaceId,
          title,
          askPersonaId: DEFAULT_CHAT_PERSONA_ID,
          ...(defaultAskPromptId ? { defaultAskPromptId } : {}),
        },
      });

      const sessionId = response.askSession?.id;
      if (!sessionId) {
        const err = new Error('Create Ask Session did not return an askSession id');
        console.error('[useLandingPageWhatToDo]', err.message);
        throw err;
      }

      onProgress?.(ASK_OPEN_PROGRESS_PERSONA);

      const trimmedQuestion = inputText?.trim() ?? '';
      if (trimmedQuestion) {
        onProgress?.(ASK_OPEN_PROGRESS_ASKING);
        await invokeAsk({
          getAccessToken,
          askSessionId: sessionId,
          data: {
            input: trimmedQuestion,
            inputTypeIndex: 0,
            variables: [],
            selectedDocuments: [],
            documentCollections: [],
            workspaceId,
            askPromptId:
              response.askSession?.initialAskPromptId ??
              defaultAskPromptId ??
              '',
            askType: AskType.MESSAGE,
          },
        });
      }

      onProgress?.(ASK_OPEN_PROGRESS_GO_WORKSPACE);
      setCurrentWorkspaceId(workspaceId);
      onProgress?.(ASK_OPEN_PROGRESS_OPEN_ASK);
      navigate(`/workspace/${workspaceId}/ask-session/${sessionId}`);
    },
    [getAccessToken, navigate, setCurrentWorkspaceId],
  );

  const executeOption = useCallback(
    async (option: ResultOption): Promise<string> => {
      const action = String(option.action).toLowerCase();
      const feature = String(option.feature).toLowerCase();
      const workspaceId = option.workspaceId;

      if (!workspaceId) {
        console.warn('[useLandingPageWhatToDo] Result option missing workspaceId:', option);
        return option.description || 'Action executed.';
      }

      setCurrentWorkspaceId(workspaceId);

      if (feature === WhatToDoFeature.ASK) {
        if (action === WhatToDoAction.CREATE) {
          const inputText =
            typeof option.prompt === 'string' && option.prompt.trim().length > 0
              ? option.prompt
              : '';
          try {
            await openNewAskSessionWithInput({
              workspaceId,
              inputText,
              onProgress: setSuggestedActionProgress,
            });
            return (
              option.description ||
              'Opening AI conversation in the selected workspace...'
            );
          } catch (err: unknown) {
            const normalizedError = normalizeError(
              err,
              'Failed to start Ask from suggested action',
            );
            console.error('[useLandingPageWhatToDo] ASK/CREATE failed:', normalizedError);
            handleError(
              normalizedError,
              'Failed to open Ask session. Please try again.',
            );
            return 'Failed to open Ask session. Please try again.';
          }
        }

        if (action === WhatToDoAction.OPEN && option.id) {
          navigate(`/workspace/${workspaceId}/ask-session/${option.id}`);
          return option.description || 'Opening your Ask session...';
        }
      }

      if (
        feature === WhatToDoFeature.PROMPT &&
        action === WhatToDoAction.CREATE
      ) {
        const inputText = option.prompt ?? option.description ?? '';
        try {
          await openNewAskSessionWithInput({
            workspaceId,
            inputText,
            defaultAskPromptId: option.id,
            onProgress: setSuggestedActionProgress,
          });
          return option.description || 'Opening your prompt in Ask...';
        } catch (err: unknown) {
          const normalizedError = normalizeError(
            err,
            'Failed to start Ask from prompt suggestion',
          );
          console.error('[useLandingPageWhatToDo] PROMPT/CREATE failed:', normalizedError);
          handleError(
            normalizedError,
            'Failed to open Ask session. Please try again.',
          );
          return 'Failed to open Ask session. Please try again.';
        }
      }

      if (
        feature === WhatToDoFeature.DOCUMENT ||
        feature === WhatToDoFeature.COLLECTION
      ) {
        navigate(`/workspace/${workspaceId}/documents`);
        return option.description || 'Opening workspace documents...';
      }

      if (feature === WhatToDoFeature.WORKSPACE) {
        navigate(`/workspace/${workspaceId}`);
        return option.description || 'Opening workspace...';
      }

      navigate(`/workspace/${workspaceId}`);
      return option.description || 'Action executed.';
    },
    [navigate, setCurrentWorkspaceId, openNewAskSessionWithInput, handleError],
  );

  const handleQuestionSubmit = useCallback(
    async (question: string): Promise<string> => {
      try {
        const trimmedQuestion = question.trim().toLowerCase();
        const optionMatch = trimmedQuestion.match(/(?:option\s*)?(\d+)/);

        if (optionMatch && lastResultOptions.length > 0) {
          const selectedIndex = parseInt(optionMatch[1], 10) - 1;
          if (selectedIndex >= 0 && selectedIndex < lastResultOptions.length) {
            const selectedOption = lastResultOptions[selectedIndex];
            const result = await executeOption(selectedOption);
            setLastResultOptions([]);
            return result;
          }
          return `Invalid option number. Please choose between 1 and ${lastResultOptions.length}.`;
        }

        const response = await postWhatToDo({
          getAccessToken,
          userInput: question,
        });

        if (!response?.success) {
          const errorMessage =
            response?.error || 'Sorry, I could not process your request.';
          console.error('[useLandingPageWhatToDo] API error:', errorMessage);
          return errorMessage;
        }

        const resultOptions = getResultOptionsFromResponse(response);

        if (!Array.isArray(resultOptions) || resultOptions.length === 0) {
          setLastResultOptions([]);
          clearCapturedResultOptions();
          return 'I could not find any suggestions for that question.';
        }

        setLastResultOptions(resultOptions);
        captureResultOptionsForHistory(resultOptions);

        if (resultOptions.length === 1) {
          return "Here's one thing you can do — pick it below.";
        }

        return 'Here are some things you can do — pick one below.';
      } catch (error: unknown) {
        const normalizedError = normalizeError(
          error,
          'Failed to resolve landing page suggestions',
        );
        console.error('[useLandingPageWhatToDo] handleQuestionSubmit failed:', normalizedError);
        handleError(
          normalizedError,
          'Sorry, something went wrong while figuring out what to do.',
        );
        setLastResultOptions([]);
        clearCapturedResultOptions();
        return 'Sorry, something went wrong while figuring out what to do.';
      }
    },
    [
      executeOption,
      getAccessToken,
      handleError,
      lastResultOptions,
      captureResultOptionsForHistory,
      clearCapturedResultOptions,
    ],
  );

  const handleChatClick = useCallback(
    async (question: string) => {
      const privateWorkspace = workspaces.find(
        (w) => w.workspaceType === 'Private',
      );
      const workspaceId = privateWorkspace?.id;
      if (!workspaceId) {
        const warningMessage =
          'No private workspace found. Create or refresh workspaces and try again.';
        console.warn('[useLandingPageWhatToDo]', warningMessage);
        handleWarning(warningMessage);
        return;
      }

      try {
        await openNewAskSessionWithInput({
          workspaceId,
          inputText: question ?? '',
          onProgress: setSuggestedActionProgress,
        });
      } catch (err: unknown) {
        const normalizedError = normalizeError(
          err,
          'Failed to open Chat session from landing page',
        );
        console.error('[useLandingPageWhatToDo] handleChatClick failed:', normalizedError);
        handleError(
          normalizedError,
          'Failed to open Chat session. Please try again.',
        );
      } finally {
        setSuggestedActionProgress(null);
      }
    },
    [workspaces, openNewAskSessionWithInput, handleError, handleWarning],
  );

  const handleSuggestedActionOpenInChat = useCallback(
    async (prompt: string) => {
      if (isOpeningInChat) return;
      setIsOpeningInChat(true);
      try {
        const trimmed = prompt.trim();
        const matched = lastResultOptions.find(
          (o) =>
            typeof o.prompt === 'string' && o.prompt.trim() === trimmed,
        );
        if (matched?.workspaceId) {
          const feature = String(matched.feature).toLowerCase();
          await openNewAskSessionWithInput({
            workspaceId: matched.workspaceId,
            inputText:
              (typeof matched.prompt === 'string' ? matched.prompt : '') ||
              prompt,
            defaultAskPromptId:
              feature === WhatToDoFeature.PROMPT ? matched.id : undefined,
            onProgress: setSuggestedActionProgress,
          });
          return;
        }
        await handleChatClick(prompt);
      } catch (err: unknown) {
        const normalizedError = normalizeError(
          err,
          'Failed to open Chat from suggested action',
        );
        console.error(
          '[useLandingPageWhatToDo] handleSuggestedActionOpenInChat failed:',
          normalizedError,
        );
        handleError(
          normalizedError,
          'Failed to open Chat session. Please try again.',
        );
      } finally {
        setIsOpeningInChat(false);
        setSuggestedActionProgress(null);
      }
    },
    [
      handleChatClick,
      isOpeningInChat,
      lastResultOptions,
      openNewAskSessionWithInput,
      handleError,
    ],
  );

  const handleSuggestedActionSelect = useCallback(
    async (selected: SuggestedActionPayload) => {
      const resolved =
        resolveResultOptionFromSuggestedSelection(selected, lastResultOptions) ??
        (selected as ResultOption);
      try {
        await executeOption(resolved);
      } finally {
        setLastResultOptions([]);
        setSuggestedActionProgress(null);
      }
    },
    [executeOption, lastResultOptions],
  );

  const suggestedActionsData = lastResultOptions.map((option) =>
    mapResultOptionToSuggestedAction(
      option,
      getWorkspaceDisplayInfo(option.workspaceId).name,
    ),
  );

  const openInChatWithProgress = useCallback(
    async (params: {
      workspaceId: string;
      promptText: string;
      defaultAskPromptId?: string;
    }) => {
      setIsOpeningInChat(true);
      try {
        await openNewAskSessionWithInput({
          workspaceId: params.workspaceId,
          inputText: params.promptText,
          defaultAskPromptId: params.defaultAskPromptId,
          onProgress: setSuggestedActionProgress,
        });
      } finally {
        setIsOpeningInChat(false);
        setSuggestedActionProgress(null);
      }
    },
    [openNewAskSessionWithInput],
  );

  return {
    lastResultOptions,
    setLastResultOptions,
    isOpeningInChat,
    suggestedActionProgress,
    openInChatWithProgress,
    setSuggestedActionProgress,
    openNewAskSessionWithInput,
    executeOption,
    handleQuestionSubmit,
    handleChatClick,
    handleSuggestedActionOpenInChat,
    handleSuggestedActionSelect,
    suggestedActionsData,
  };
};
